import type { EligibilityFormData } from '../../components/eligibilitySteps/types'
import {
  formatSsnLastFour,
  getEnrollmentFieldEditability,
  getDriverLicenseDocumentFileName,
  getEnrollmentBoolean,
  getEnrollmentNumber,
  getEnrollmentStringArray,
  getEnrollmentString,
  normalizeStateName,
  prefer,
  preferBoolean
} from './helpers'
import {
  isoToFormDate,
  normalizeFormDateValue,
  normalizeTrainingModeForForm,
  normalizeTransmissionForForm
} from './formatting'
import {
  getMaxRestorableStepForStatus,
  hasCompletedEnrollment,
  hasSignedEnrollment,
  normalizeCurrentStepForUi
} from './status'
import type { HydrationInput, HydrationResult } from './types'

export const hydrateEnrollmentForm = ({
  profile,
  enrollment,
  previous
}: HydrationInput): HydrationResult => {
  const personalData = profile?.personalData
  const sourceEnrollment = enrollment ?? profile?.currentEnrollment ?? null
  const location =
    getEnrollmentString(sourceEnrollment, [
      'selectedLocationExternalId',
      'trainingLocationExternalId',
      'locationExternalId'
    ]) || ''
  const selectedProgram =
    getEnrollmentString(sourceEnrollment, [
      'selectedProgram',
      'priceListItemId',
      'programExternalId'
    ]) || ''
  const transmission =
    getEnrollmentString(sourceEnrollment, ['transmissionType', 'preferredTransmission']) || ''
  const trainingStartDate = normalizeFormDateValue(
    getEnrollmentString(sourceEnrollment, [
      'preferredStartDate',
      'trainingStartDate',
      'confirmedStartDate'
    ])
  )
  const yardDays = getEnrollmentStringArray(sourceEnrollment, ['yardDays']) || []
  const driverLicenseDocumentFileName =
    getDriverLicenseDocumentFileName(sourceEnrollment) || previous.driverLicenseDocumentFileName

  const formData: EligibilityFormData = {
    ...previous,
    email: prefer(
      profile?.email,
      prefer(getEnrollmentString(sourceEnrollment, ['email']), previous.email)
    ),
    dateOfBirth:
      isoToFormDate(getEnrollmentString(sourceEnrollment, ['dateOfBirth'])) ||
      isoToFormDate(personalData?.dateOfBirth) ||
      previous.dateOfBirth,
    isCitizen: preferBoolean(
      getEnrollmentBoolean(sourceEnrollment, ['usCitizen']),
      preferBoolean(personalData?.usCitizen, previous.isCitizen)
    ),
    gender: prefer(
      getEnrollmentString(sourceEnrollment, ['gender']),
      prefer(personalData?.gender, previous.gender)
    ),
    ethnicity: prefer(
      getEnrollmentString(sourceEnrollment, ['ethnicity']),
      prefer(personalData?.ethnicity, previous.ethnicity)
    ),
    educationLevel: prefer(
      getEnrollmentString(sourceEnrollment, ['educationLevel']),
      prefer(personalData?.educationLevel, previous.educationLevel)
    ),
    driverLicenseState:
      normalizeStateName(getEnrollmentString(sourceEnrollment, ['driversLicenseState'])) ||
      normalizeStateName(personalData?.driversLicenseState) ||
      previous.driverLicenseState,
    driverLicenseNumber: prefer(
      getEnrollmentString(sourceEnrollment, ['driversLicenseNumber']),
      prefer(personalData?.driversLicenseNumber, previous.driverLicenseNumber)
    ),
    licenseIssueDate:
      isoToFormDate(getEnrollmentString(sourceEnrollment, ['driversLicenseIssueDate'])) ||
      isoToFormDate(personalData?.driversLicenseIssueDate) ||
      previous.licenseIssueDate,
    licenseExpirationDate:
      isoToFormDate(getEnrollmentString(sourceEnrollment, ['driversLicenseExpiry'])) ||
      isoToFormDate(personalData?.driversLicenseExpiry) ||
      previous.licenseExpirationDate,
    ssn: formatSsnLastFour(personalData?.ssnLastFour) || previous.ssn,
    emergencyContactName: prefer(
      getEnrollmentString(sourceEnrollment, ['emergencyContactName']),
      prefer(personalData?.emergencyContactName, previous.emergencyContactName)
    ),
    emergencyContactPhone: prefer(
      getEnrollmentString(sourceEnrollment, ['emergencyContactPhone']),
      prefer(personalData?.emergencyContactPhone, previous.emergencyContactPhone)
    ),
    zipCode: prefer(
      getEnrollmentString(sourceEnrollment, ['zipCode']),
      prefer(personalData?.zipCode, previous.zipCode)
    ),
    location: location || previous.location,
    selectedProgram: selectedProgram || previous.selectedProgram,
    transmissionType: normalizeTransmissionForForm(transmission) || previous.transmissionType,
    endorsements: getEnrollmentStringArray(sourceEnrollment, ['endorsements']) || previous.endorsements,
    trainingMode:
      normalizeTrainingModeForForm(getEnrollmentString(sourceEnrollment, ['trainingMode'])) ||
      previous.trainingMode,
    hasLaptop: getEnrollmentBoolean(sourceEnrollment, ['hasLaptop']) ?? previous.hasLaptop,
    trainingStartDate: trainingStartDate || previous.trainingStartDate,
    yardDays: yardDays.length > 0 ? yardDays : previous.yardDays,
    driverLicenseDocumentFileName
  }

  return {
    formData,
    fieldEditability: getEnrollmentFieldEditability(sourceEnrollment),
    enrollmentId: getEnrollmentNumber(sourceEnrollment, ['id', 'enrollmentId']),
    sessionToken: getEnrollmentString(sourceEnrollment, ['sessionToken']) || null,
    currentStep: normalizeCurrentStepForUi(sourceEnrollment),
    maxRestorableStep: getMaxRestorableStepForStatus(sourceEnrollment),
    signatureCompleted: hasSignedEnrollment(sourceEnrollment),
    enrollmentCompleted: hasCompletedEnrollment(sourceEnrollment),
    uploadedDocumentFiles: driverLicenseDocumentFileName
      ? { driverLicense: driverLicenseDocumentFileName }
      : {}
  }
}
